import * as React from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { Manager } from "../../types/users";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  useGetManagerByIdQuery,
  useAddManagerMutation,
  useEditManagerMutation,
} from "../../services/manager.service";
import { BaseModal, StyledTextField } from "../../components/global";
import { useUILabel } from "../../hooks/useUILabel";

interface ModalManagerFormProps {
  open: boolean;
  onClose: () => void;
  data: Manager | null;
}

const ModalManagerForm = ({ open, onClose, data }: ModalManagerFormProps) => {
  const { data: admin } = useGetManagerByIdQuery(data?.id!, {
    skip: !data?.id,
  });

  const [add, { isLoading: adding }] = useAddManagerMutation();
  const [edit, { isLoading: editing }] = useEditManagerMutation();
  
  const createManagerLabel = useUILabel("manager.createManager");
  const editManagerLabel = useUILabel("manager.editManager");
  const createLabel = useUILabel("common.create");
  const updateLabel = useUILabel("common.update");
  const cancelLabel = useUILabel("common.cancel");
  const firstNameLabel = useUILabel("manager.firstName");
  const lastNameLabel = useUILabel("manager.lastName");
  const phoneNumberLabel = useUILabel("common.phoneNumber");
  const emailLabel = useUILabel("common.email");
  const passwordLabel = useUILabel("common.password");
  const firstNameRequiredLabel = useUILabel("manager.firstNameRequired");
  const lastNameRequiredLabel = useUILabel("manager.lastNameRequired");
  const emailRequiredLabel = useUILabel("manager.emailRequired");
  const invalidEmailAddressLabel = useUILabel("manager.invalidEmailAddress");
  const passwordRequiredLabel = useUILabel("manager.passwordRequired");
  const leaveBlankIfDontWantToChangePasswordLabel = useUILabel("manager.leaveBlankIfDontWantToChangePassword");
  const passwordRequirementLabel = useUILabel("manager.passwordRequirement");

  const formRef = React.useRef<HTMLFormElement>(null);
  const {
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        if (!payload.password) delete payload.password;
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("first_name", data?.first_name || "");
      setValue("last_name", data?.last_name || "");
      setValue("phone_number", data?.phone_number || "");
      setValue("email", data?.email || "");
      setValue("password", "");
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (admin) {
      setValue("first_name", admin?.first_name || "");
      setValue("last_name", admin?.last_name || "");
      setValue("phone_number", admin?.phone_number || "");
      setValue("email", admin?.email || "");
    setValue("password", "");
    }
  }, [admin, setValue]);

  const footer = (
    <>
      <Button onClick={onClose} disabled={adding || editing}>
        {cancelLabel}
      </Button>
      <LoadingButton
        type="submit"
        variant="contained"
        color="success"
        loading={adding || editing}
        form="manager-form"
      >
        {!data ? createLabel : updateLabel}
      </LoadingButton>
    </>
  );

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={!data ? createManagerLabel : editManagerLabel}
      footer={footer}
      maxWidth="xs"
      useGlassCard={true}
    >
      <Box
        component="form"
        id="manager-form"
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        sx={{ p: 3 }}
      >
        <Stack spacing={2.5}>
            <Controller
              name="first_name"
              control={control}
            rules={{ required: firstNameRequiredLabel }}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                  label={firstNameLabel}
                value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.first_name}
                helperText={errors.first_name?.message as string}
                />
              )}
            />
            <Controller
              name="last_name"
              control={control}
            rules={{ required: lastNameRequiredLabel }}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                  label={lastNameLabel}
                value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!errors.last_name}
                helperText={errors.last_name?.message as string}
                />
              )}
            />
            <Controller
              name="phone_number"
              control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                  label={phoneNumberLabel}
                value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
            rules={{ 
              required: emailRequiredLabel,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: invalidEmailAddressLabel
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                  label={emailLabel}
                value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                  type="email"
                  error={!!errors.email}
                helperText={errors.email?.message as string}
                />
              )}
            />
          <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
            {passwordRequirementLabel}
            </Typography>
            <Controller
              name="password"
              control={control}
            rules={{ required: !data ? passwordRequiredLabel : false }}
            render={({ field: { onChange, onBlur, value } }) => (
              <StyledTextField
                  label={passwordLabel}
                value={value || ""}
                  fullWidth
                  onChange={onChange}
                  onBlur={onBlur}
                type="password"
                  error={!!errors.password}
                  helperText={
                    !data
                    ? (errors.password?.message as string)
                      : leaveBlankIfDontWantToChangePasswordLabel
                  }
                />
              )}
            />
        </Stack>
          </Box>
    </BaseModal>
  );
};
export default ModalManagerForm;
